import { useEffect, useRef, useState } from "react"
import type { RefObject } from "react"

/**
 * `useElementHeight` — observe an element and report its current pixel height.
 *
 * Returns `[ref, height]`: attach `ref` to the element you want to measure and
 * read `height` (integer `offsetHeight`, border-box) reactively. The value
 * updates on any size change — content reflow, font-scaling, viewport
 * rotation — via a `ResizeObserver` over the element.
 *
 * The default height is `0`, which is also the graceful fallback in
 * environments without `ResizeObserver` (SSR / jsdom). The first real
 * measurement is delivered by the observer's initial callback rather than
 * synchronously in the effect body, so the hook never trips
 * `react-hooks/set-state-in-effect`. `setHeight` bails when the value is
 * unchanged, so an observed-but-static element causes no re-render churn.
 *
 * OKS-694 — used by `app-layout.tsx` to measure the app-wide banner stack and
 * publish its height as the `--banner-stack-h` CSS custom property, which the
 * viewport-fixed sidebar rail consumes to start below the banners instead of
 * overlapping them. Height `0` (no banners) ⇒ the sidebar offset is `0px`,
 * i.e. identical to the pre-fix geometry.
 */
export function useElementHeight<T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T | null>,
  number,
] {
  const ref = useRef<T | null>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return undefined

    const measure = () => {
      const next = el.offsetHeight
      setHeight((prev) => (prev === next ? prev : next))
    }
    // observe() schedules an initial callback → first measurement (no
    // synchronous setState in this effect body).
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return [ref, height]
}
